import { format as formatTime, parseISO } from 'date-fns'
import { CurrencyPosition, Marketplace, MarketplaceI, Marketplaces } from './marketplaces'

const formatters: { [key: number]: Intl.NumberFormat } = {}

export const nullableMoney = (
    value: number | null,
    marketplace: MarketplaceI | null = null,
): string | null => {
    return money(value, marketplace, true)
}

interface CurrencyCodeI {
    currency_symbol: string
    currency_position: CurrencyPosition
}

const currencyCodes: { [key: string]: CurrencyCodeI } = {
    USD: { currency_symbol: '$', currency_position: CurrencyPosition.Left },
    CAD: { currency_symbol: '$', currency_position: CurrencyPosition.Left },
    MXN: { currency_symbol: '$', currency_position: CurrencyPosition.Left },
    BRL: { currency_symbol: 'R$', currency_position: CurrencyPosition.Left },
    GBP: { currency_symbol: '£', currency_position: CurrencyPosition.Left },
    EUR: { currency_symbol: '€', currency_position: CurrencyPosition.Left },
    SEK: { currency_symbol: ' kr', currency_position: CurrencyPosition.Right },
    JPY: { currency_symbol: '¥', currency_position: CurrencyPosition.Left },
    AED: { currency_symbol: 'د.إ', currency_position: CurrencyPosition.Right },
    AUD: { currency_symbol: '$', currency_position: CurrencyPosition.Left },
}

export const moneyWithCurrencyCode = (
    value: number | null,
    currencyCode: string | null = null,
    nullable = false,
): string | null => {
    let currencySymbol = ''
    let currencyPosition = CurrencyPosition.Left

    // Defaults don't seem to work correctly, so we manually set it here
    let marketplace: MarketplaceI
    if (currencyCode == null) {
        // @ts-ignore
        if (window['marketplace']) {
            // @ts-ignore
            marketplace = window['marketplace']
        } else {
            marketplace = Marketplaces[Marketplace.UnitedStates]
        }

        currencySymbol = marketplace.currency_symbol
        currencyPosition = marketplace.currency_position
    } else {
        const currency = currencyCodes[currencyCode]
        currencySymbol = currency.currency_symbol
        currencyPosition = currency.currency_position
    }

    if (currencyPosition == CurrencyPosition.Left) {
        return format(value, 2, nullable, currencySymbol)
    } else {
        return format(value, 2, nullable, undefined, currencySymbol)
    }
}

const handleNanAndInf = (value: number | string | null): string | null => {
    if (value == 'NaN') {
        return 'NaN'
    } else if (value == 'Infinity') {
        return 'Infinity'
    } else if (value == '-Infinity') {
        return '-Infinity'
    }

    return null
}

export const money = (
    value: number | null,
    marketplace: MarketplaceI | null = null,
    nullable = false,
    decimals = 2,
): string | null => {
    const result = handleNanAndInf(value)
    if (result) {
        return result
    }

    // Defaults don't seem to work correctly, so we manually set it here
    if (marketplace == null) {
        // @ts-ignore
        if (window['marketplace']) {
            // @ts-ignore
            marketplace = window['marketplace'] as MarketplaceI
        } else {
            marketplace = Marketplaces[Marketplace.UnitedStates] as MarketplaceI
        }
    }

    if (marketplace.currency_position == CurrencyPosition.Left) {
        return format(value, decimals, nullable, marketplace.currency_symbol)
    } else {
        return format(value, decimals, nullable, undefined, marketplace.currency_symbol)
    }
}

export const cents = (
    value: number | null,
    marketplace: MarketplaceI | null = null,
): string | null => {
    const result = handleNanAndInf(value)
    if (result) {
        return result
    }

    return money(value != null ? value / 100 : null, marketplace)
}

export const nullablePercentage = (value: number | null, decimals = 2): string | null => {
    return format(value, decimals, true, '', '%')
}

export const percentage = (value: number, decimals = 2): string => {
    return format(value, decimals, false, '', '%') ?? ''
}

export const nullableNumber = (value: number | null, decimals = 0) => {
    if (value === null) {
        return null
    }

    return number(value, decimals)
}

export const number = (value: number, decimals = 0) => {
    const result = handleNanAndInf(value)
    if (result) {
        return result
    }

    const formatter = numberFormat(decimals)
    return formatter.format(value)
}

export const format = (
    value: number | null,
    decimals = 0,
    nullable = false,
    prefix = '',
    suffix = '',
): string | null => {
    if (!value) {
        if (nullable && value === null) {
            return null
        }

        value = 0
    }

    const formatter = numberFormat(decimals)

    if (value < 0) {
        return '-' + prefix + formatter.format(Math.abs(value)) + suffix
    }

    return prefix + formatter.format(value) + suffix
}

const numberFormat = (decimals: number): Intl.NumberFormat => {
    if (formatters[decimals]) {
        return formatters[decimals]
    } else {
        formatters[decimals] = Intl.NumberFormat('en-us', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        })
        return formatters[decimals]
    }
}

export const time = (value: string | null): string | null => {
    if (!value) {
        return null
    }

    return formatTime(parseISO(value), 'M/d/y HH:ii:ss a')
}

export const date = (value: number, readable?: boolean): string | null => {
    if (!value) {
        return null
    }

    const d = new Date(value * 1000)

    if (readable) {
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    }
    return d.toLocaleDateString('en-US')
}
